import React, { useState, useCallback, useEffect } from "react";
import { useLocalization } from "../../context/LocalizationContext";
import { useAuth } from "../../context/AuthContext";
import { useAppContext } from "../../context/AppContext";
import { useAIAvatarSession } from "../../context/AIAvatarSessionContext";
import { useCustomAvatarVoice } from "../../hooks/useCustomAvatarVoice";
import { useWakeWord } from "../../hooks/useWakeWord";
import { WAKE_WORD_KEYWORDS_EN, WAKE_WORD_KEYWORDS_AR } from "../../constants";
import Modal from "../shared/Modal";
import Button from "../shared/Button";
import micIcon from "../../assets/icons/AI/whiteMic.svg";
import "./BubblingAIAssistant.css";

const BubblingAIAssistant: React.FC = () => {
  const { t, isRTL } = useLocalization();
  const { isAuthenticated } = useAuth();
  const { aiLanguage } = useAppContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [autoInitialized, setAutoInitialized] = useState(false);

  const {
    videoRef,
    audioRef,
    sessionToken,
    sessionId,
    isVideoReady,
    isConnected,
    avatarSpeaking,
    transcription,
    repeat,
    initializeSession,
    isAgentConnecting,
    setIsAgentConnecting,
    agentError,
    setAgentError,
    claimWakeWord,
    releaseWakeWord,
  } = useAIAvatarSession();

  const { isRecording, startRecording, stopRecording } = useCustomAvatarVoice({
    sessionId,
    repeat,
    language: aiLanguage,
  });

  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Release the wake-word claim whenever recording ends, regardless of whether
  // it was stopped manually or automatically by the silence timeout.
  useEffect(() => {
    if (!isRecording) {
      releaseWakeWord();
    }
  }, [isRecording, releaseWakeWord]);

  useWakeWord({
    keywords: aiLanguage === "ar" ? WAKE_WORD_KEYWORDS_AR : WAKE_WORD_KEYWORDS_EN,
    lang: aiLanguage === "ar" ? "ar-SA" : "en-US",
    enabled: isConnected && !isRecording && !avatarSpeaking,
    onKeywordDetected: () => {
      if (!claimWakeWord()) return;
      setModalOpen(true);
      startRecording();
    },
  });

  const handleStartSpeaking = useCallback(async () => {
    // First time: initialize session
    if (!sessionToken && !isAgentConnecting) {
      try {
        await initializeSession();
      } catch {
        setAgentError(t("liveAvatar.failedToConnect"));
      }
      return;
    }

    // Toggle recording once session is connected
    if (isConnected) {
      if (isRecording) {
        handleStopRecording();
      } else {
        await startRecording();
      }
    }
  }, [
    sessionToken,
    isConnected,
    isRecording,
    startRecording,
    handleStopRecording,
    initializeSession,
    isAgentConnecting,
    setAgentError,
    t,
  ]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setAgentError(null);
  }, [setAgentError]);

  // Automatically initialize shared session when authenticated (idempotent; same idea as Dashboard on load)
  useEffect(() => {
    if (
      isAuthenticated &&
      !autoInitialized &&
      !sessionToken &&
      !isAgentConnecting
    ) {
      setAutoInitialized(true);
      initializeSession().catch((err) => {
        console.error("Avatar init error:", err);
        setAgentError(t("liveAvatar.failedToConnect"));
      });
    }
  }, [
    isAuthenticated,
    autoInitialized,
    sessionToken,
    isAgentConnecting,
    initializeSession,
    setAgentError,
    t,
  ]);


  if (!isAuthenticated) return null;

  const showFloatingTranscript =
    !modalOpen && Boolean(transcription && transcription.trim().length > 0);

  return (
    <>
      <div className="bubbling-ai-anchor">
        <button
          type="button"
          className="bubbling-ai-bubble"
          onClick={() => setModalOpen(true)}
          aria-label={t("settings.aiAssistant")}
        >
          <span className="bubbling-ai-bubble-glow" />
          <span className="bubbling-ai-bubble-trail" />
          <span className="bubbling-ai-bubble-avatar" />
          <audio ref={audioRef} autoPlay muted={false} />
        </button>

        {showFloatingTranscript && (
          <div className="bubbling-ai-transcription-bubble" aria-live="polite">
            <span className="bubbling-ai-transcription-text">
              {transcription}
            </span>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        customLayout
        noScroll
        closeVisible={false}
        title={null}
        message={null}
      >
        <div className="modal-wrapper">
          <div className="bubbling-ai-modal">
            <div className="bubbling-ai-modal-avatar-wrap">
              {avatarSpeaking && <div className="bubbling-ai-modal-ring" />}
              <div className="bubbling-ai-modal-inner">
                {!isVideoReady && !isAgentConnecting && !agentError && (
                  <div className="bubbling-ai-modal-placeholder" />
                )}
                {isAgentConnecting && (
                  <div className="bubbling-ai-modal-loading">
                    <span>{t("dashboard.connectingToAgent")}</span>
                  </div>
                )}
                {agentError && (
                  <div className="bubbling-ai-modal-error">
                    <span>{agentError}</span>
                  </div>
                )}
                <video
                  ref={videoRef}
                  className="bubbling-ai-modal-video"
                  playsInline
                  autoPlay
                  muted={false}
                  style={{ display: isVideoReady ? "block" : "none" }}
                  aria-label={t("settings.aiAssistant")}
                />
              </div>
            </div>
            <p className="bubbling-ai-modal-prompt">
              {transcription || t("aiAssistant.whatCanIHelp")}
            </p>
            <Button
              variant="secondary"
              icon={micIcon}
              iconPosition="start"
              className={`bubbling-ai-modal-start-speaking${isRecording ? " bubbling-ai-modal-start-speaking--listening" : ""}`}
              onClick={handleStartSpeaking}
              disabled={isAgentConnecting}
              iconStyle={
                isRTL
                  ? { transform: "scaleX(-1)", marginLeft: "1vw", marginRight: 0 }
                  : { marginRight: "1vw" }
              }
            >
              {isAgentConnecting
                ? t("dashboard.connectingToAgent")
                : isRecording
                  ? t("dashboard.listening")
                  : t("dashboard.startSpeaking")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BubblingAIAssistant;

